'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  BannerSlot5Modal,
  Breadcrumbs,
  DispensaryLocations,
  FAQSection,
  Header,
  PageShowcasesProvider,
  JoinTheFamSection,
  RecentDealsSection,
  ReviewsSection,
  SponsoredBanners,
  SponsoredBannersProvider,
} from '@/components';
import Button from '@/components/ui/Button';
import ProductShowcaseSection from '@/components/sections/ProductShowcaseSection';
import DankLifestyleSection from '@/components/sections/DankLifestyleSection';
import WhyDankDeliverySection from '@/components/sections/WhyDankDeliverySection';
import HolyShitDealsSection from '@/components/sections/HolyShitDealsSection';
import CategorySection from '@/components/sections/CategorySection';
import DeliveryComparisonSection from '@/components/sections/DeliveryComparisonSection';
import SelectDeliveryMenuSection from '@/components/sections/SelectDeliveryMenuSection';
import type { Store } from '@/lib/types/stores';
import { DEFAULT_CATEGORY_TILES as categories } from "@/lib/category-tiles";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';


export default function HowDeliveryWorksPage() {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <SponsoredBannersProvider>
      <PageShowcasesProvider endpoint="/api/how-delivery-works/product-showcases">
        <div className="min-h-screen">
          <Header stores={stores} base={API_BASE} />

          <main className="home-page sm:space-y-36 space-y-16 pb-16">
            <section
              className="relative mt-4 overflow-hidden sm:mt-6"
              aria-labelledby="how-delivery-hero-heading"
            >
              <div className="mx-auto w-full px-4 sm:px-8">
                <div className="relative w-full overflow-hidden rounded-2xl bg-brand-100 sm:rounded-2xl">
                  <div className="pointer-events-none absolute inset-0 z-0">
                    <Image
                      src="/media/pages/hero-bg-image.webp"
                      alt=""
                      fill
                      priority
                      className="object-cover object-center opacity-[0.05]"
                      sizes="100vw"
                      aria-hidden
                    />
                  </div>
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/75"
                    aria-hidden
                  />

                  <div className="relative z-[2] flex flex-col gap-6 p-4 sm:flex-row sm:items-end sm:justify-between sm:gap-8 sm:p-6 lg:p-8">
                    <div className="min-w-0 flex-1">
                      <Breadcrumbs items={[{ label: 'How delivery works' }]} tone="onDark" />

                      <h1
                        id="how-delivery-hero-heading"
                        className="text-h2-mobile font-bold leading-tight text-white lg:text-h2-desktop"
                      >
                        Dank Cannabis Weed Delivery
                      </h1>

                      <p className="mt-4 text-body-16-desktop font-normal leading-relaxed text-white/90">
                        Experience Alberta&apos;s fastest legal weed delivery. We offer same-day delivery in as little as 45
                        minutes to Calgary, Okotoks, Chestermere, High River, and Aldersyde. Not local? We provide reliable
                        Alberta mail order marijuana shipping to any postal code. Fully AGLC-licensed. Sign up in seconds to
                        access the best menu in the game and get your order started today.
                      </p>
                    </div>

                    <div className="w-full sm:mb-0 sm:w-auto sm:shrink-0">
                      <Button
                        asChild
                        variant="ghost"
                        size="lg"
                        className="rounded-xl px-6"
                      >
                        <Link href="/search">Start your order</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-[60px] px-4 sm:px-8">
                <SelectDeliveryMenuSection />
              </div>

              <div className="mt-6 w-full px-4 sm:px-6 lg:px-8" aria-label="Sponsored content">
                <SponsoredBanners position="how-delivery-works-1" limit={1} className="w-full" />
              </div>
            </section>

            <WhyDankDeliverySection />

            <ProductShowcaseSection slotKey="afterHero" />

            <HolyShitDealsSection />

            <CategorySection categories={categories as any} />

            <DeliveryComparisonSection />

            <section className="w-full px-4 sm:px-6 lg:px-8" aria-label="Sponsored content">
              <SponsoredBanners position="how-delivery-works-2" limit={1} className="w-full" />
            </section>

            <ProductShowcaseSection slotKey="afterPopularBrands" />

            <RecentDealsSection />

            <div className='!px-4 sm:!px-8'>
              <DispensaryLocations base={API_BASE} />
            </div>

            <ProductShowcaseSection slotKey="afterCommunity" />

            <DankLifestyleSection apiPath="/api/how-delivery-works/merch" />

            <ReviewsSection />

            <FAQSection />

            <section className="w-full px-4 sm:px-6 lg:px-8" aria-label="Sponsored content">
              <SponsoredBanners position="how-delivery-works-3" limit={1} className="w-full" />
            </section>

            <JoinTheFamSection />
          </main>
        </div>
      </PageShowcasesProvider>
    </SponsoredBannersProvider>
  );
}
